import {
  BufferGeometry,
  CanvasTexture,
  Group,
  Line,
  Mesh,
  SphereGeometry,
  Sprite,
  SpriteMaterial,
  Vector3,
} from 'three';
import type { Box3, Color, MeshBasicMaterial } from 'three';

import { INTERSECTION_SCALE_FACTOR } from '../ViewPort';
import type ViewPort from '../ViewPort';
import { getOrCreateThreeColor } from '../colors';
import { ENTITY_TYPES, PART_VIEWER_MODES, UNITS } from '../constants';
import { disposeThreeJS, isClose, isCloseVectors } from '../geom-utils';
import { OptimalBoundingBox } from '../loaders/OptimalBoundingBox';
import { AssemblyInstance } from '../loaders/assemblyInstance';
import { Body } from '../loaders/body';
import { STLBody } from '../loaders/stl/STLBody';
import { getMeshBasicMaterial } from '../materials';
import { getLineMaterial } from '../materials';
import {
  areObjectsDifferentForKeys,
  convert,
  makeDimensionalStringWithUnits,
  roundFloatingPoint,
} from '../utils';
import {
  performCaliperMeasurementStep,
  performCaliperMeasurementStl,
} from './caliper-utils';
import type {
  AnyCaliperObject,
  Comparison,
  MeasurementResult,
} from './caliper-utils';
import type { Renderer, RendererState } from './renderer';

const MEASUREMENT_FONT_SIZE = 64;
const MEASUREMENT_CANVAS_WIDTH = 16 * MEASUREMENT_FONT_SIZE;
const MEASUREMENT_CANVAS_HEIGHT = 2 * MEASUREMENT_FONT_SIZE;
const MEASUREMENT_HEIGHT_BOTTOM_PADDING_FONT_FACTOR = 0.1;
const MEASUREMENT_WIDTH_PADDING_FACTOR = 0.2;
// factor of top-to-bottom canvas viewport to apply as scale of vertex objects
const VERTEX_RADIUS_SCALE_FACTOR = 0.008;
const SPRITE_SCALE_FACTOR = 70;

type TextSpriteMaterialData = {
  message: string;
  messageFillStyle?: string;
  header: string;
  headerFillStyle?: string;
  shouldFillBackground?: boolean;
  backgroundFillStyle: string;
};

type MeasurementObjectData = {
  startPoint: Vector3;
  endPoint: Vector3;

  comparison?: Comparison;
  estimated?: boolean;
  selections?: [AnyCaliperObject, AnyCaliperObject];
  lineWidth?: number;
  lineColor?: Color;
  sphereColor?: Color;
};

const defaultMeasurementColor = getOrCreateThreeColor(0xfafafa);
const defaultVectorEditColor = getOrCreateThreeColor(0x0083b7);
const defaultXDimensionsColor = getOrCreateThreeColor(0xff0000);
const defaultYDimensionsColor = getOrCreateThreeColor(0x00ff00);
const defaultZDimensionsColor = getOrCreateThreeColor(0x0000ff);

const sphereGeometry = new SphereGeometry(1, 16, 16);

export class MeasurementObject {
  public readonly id: string;
  public readonly rawLength: number;
  public readonly group: Group; //contains this.line, this.s1, this.s2, this.sprite
  public readonly comparison?: Comparison; //referenced by onNewMeasurementComparison in viewer-component.ts
  public readonly estimated?: boolean; //set when measurement is based off of tessellation rather than caliper -- shows a tilde in-front of measurement
  public readonly selections?: [AnyCaliperObject, AnyCaliperObject]; //not used for MeasurementObject in OptimalBoundingBox or Dimensions

  //Note: line, s1, s2, & sprite are in this.group
  private readonly line: Line;
  private readonly s1: Mesh<SphereGeometry, MeshBasicMaterial>;
  private readonly s2: Mesh<SphereGeometry, MeshBasicMaterial>;
  private readonly sprite: Sprite;

  constructor({
    startPoint,
    endPoint,
    comparison = undefined,
    estimated = false,
    selections = undefined, //not used for OptimalBoundingBox or Dimensions
    lineColor = defaultMeasurementColor,
    lineWidth = 3,
    sphereColor = defaultMeasurementColor,
  }: MeasurementObjectData) {
    this.id = Math.random().toString(32).slice(2);
    if (comparison) {
      this.comparison = comparison;
    }
    if (estimated) {
      this.estimated = estimated;
    }
    if (selections) {
      this.selections = selections;
    }

    const separationVector = new Vector3().subVectors(endPoint, startPoint);
    this.rawLength = separationVector.length();

    const lineMaterial = getLineMaterial(lineColor, lineWidth);
    const lineGeometry = new BufferGeometry().setFromPoints([
      startPoint,
      endPoint,
    ]);
    this.line = new Line(lineGeometry, lineMaterial);

    const sphereMaterial = getMeshBasicMaterial(sphereColor);
    this.s1 = new Mesh<SphereGeometry, MeshBasicMaterial>(
      sphereGeometry,
      sphereMaterial,
    );
    this.s2 = new Mesh<SphereGeometry, MeshBasicMaterial>(
      sphereGeometry,
      sphereMaterial,
    );
    this.s1.position.copy(startPoint);
    this.s2.position.copy(endPoint);

    const midpoint = new Vector3(
      (startPoint.x + endPoint.x) / 2,
      (startPoint.y + endPoint.y) / 2,
      (startPoint.z + endPoint.z) / 2,
    );
    this.sprite = new Sprite(new SpriteMaterial()); //instantiated with temporary SpriteMaterial until this.makeSpriteMaterial() is called
    this.sprite.position.copy(midpoint);

    this.group = new Group();
    this.group.visible = false;
    this.group.add(this.line, this.s1, this.s2, this.sprite);
    this.group.name = this.id;
  }

  public makeSpriteMaterial({
    displayUnits = UNITS.IN,
    nativeUnits = UNITS.MM,
    dimensionalPrecision = 3,
    displayScale = 1.0,
    messageFillStyle = 'black',
    messageHeader = '',
    messageHeaderFillStyle = 'black',
    shouldFillBackground = true,
    backgroundFillStyle = 'rgb(255, 255, 255, 0.5)',
  }: {
    displayUnits?: UNITS;
    nativeUnits?: UNITS;
    dimensionalPrecision?: number;
    displayScale?: number;
    messageFillStyle?: string;
    messageHeader?: string;
    messageHeaderFillStyle?: string;
    shouldFillBackground?: boolean;
    backgroundFillStyle?: string;
  }): void {
    const lengthString =
      (this.estimated ? '~' : '') +
      makeDimensionalStringWithUnits({
        value: roundFloatingPoint(
          convert(this.rawLength, displayUnits, nativeUnits, 1) * displayScale,
          dimensionalPrecision,
        ),
        order: 1,
        displayUnits,
        dimensionalPrecision,
        dropZeroes: false,
      });

    const spriteMaterial = this.makeTextSpriteMaterial({
      message: lengthString,
      messageFillStyle,
      header: messageHeader,
      headerFillStyle: messageHeaderFillStyle,
      shouldFillBackground,
      backgroundFillStyle,
    });

    // Dispose of old textures and material if sprite is being updated
    // .dispose() must be called explicitly on the Texture (stored on the material.map property)
    this.sprite.material.map?.dispose();
    this.sprite.material.dispose();

    this.sprite.material = spriteMaterial;
  }

  public scaleForOrthographicCamera({
    viewport,
    vertexSizeFactor = 0.006,
  }: {
    viewport: ViewPort;
    vertexSizeFactor?: number;
  }): void {
    // have lobes occupy certain percent of screen height wise
    const radius = viewport.getVertexRadius(vertexSizeFactor);
    this.s1.scale.set(radius, radius, radius);
    this.s2.scale.set(radius, radius, radius);

    // sprite occupies more vertical FOV
    const scaleFactor = radius * SPRITE_SCALE_FACTOR;
    this.sprite.scale.set(
      scaleFactor,
      (scaleFactor * MEASUREMENT_CANVAS_HEIGHT) / MEASUREMENT_CANVAS_WIDTH,
      1,
    );
  }

  //When done with measurement, it should be disposed
  public dispose() {
    disposeThreeJS(this.line, this.sprite);
    //Don't this.s1.geometry.dispose() or this.s2.geometry.dispose() because the sphere geometry is reused for each instance of MeasurementObject
    if (this.group) {
      this.group.clear();
    }
  }

  private makeTextSpriteMaterial({
    message,
    messageFillStyle = 'black',
    header,
    headerFillStyle = 'black',
    shouldFillBackground = true,
    backgroundFillStyle = 'rgb(255, 255, 255, 0.5)',
  }: TextSpriteMaterialData): SpriteMaterial {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d') as CanvasRenderingContext2D; //never null
    canvas.width = MEASUREMENT_CANVAS_WIDTH;
    canvas.height = MEASUREMENT_CANVAS_HEIGHT;
    const fontsize = MEASUREMENT_FONT_SIZE;
    const fontface = 'Arial';
    context.font = `${fontsize}px ${fontface}`;
    const headerWidth = context.measureText(header).width;
    const metrics = context.measureText(header + message);
    const textWidth = metrics.width;

    if (shouldFillBackground) {
      // draw a semi-transparent box in the center of the canvas
      // NOTE: rectangle gets drawn as if starting point is top left
      context.fillStyle = backgroundFillStyle;
      context.fillRect(
        canvas.width / 2 -
          textWidth / 2 -
          MEASUREMENT_WIDTH_PADDING_FACTOR * fontsize,
        canvas.height / 2 - fontsize / 2,
        textWidth + 2 * MEASUREMENT_WIDTH_PADDING_FACTOR * fontsize,
        fontsize + MEASUREMENT_HEIGHT_BOTTOM_PADDING_FONT_FACTOR * fontsize * 2,
      );
    }

    // draw text in the center of the created canvas
    // NOTE: text gets drawn as if the starting point is bottom left of text
    context.fillStyle = headerFillStyle;
    context.fillText(
      header,
      canvas.width / 2 - textWidth / 2,
      canvas.height / 2 + fontsize / 2,
    );
    context.fillStyle = messageFillStyle;
    context.fillText(
      message,
      canvas.width / 2 - textWidth / 2 + headerWidth,
      canvas.height / 2 + fontsize / 2,
    );

    // translate the canvas so its center sits right over the midpoint of the intersection
    context.translate(-canvas.width / 2, -canvas.height / 2);

    const texture = new CanvasTexture(canvas);

    const spriteMaterial = new SpriteMaterial({ map: texture });

    // turn off depth test so it is visible on top of everything
    // even when behind other entities
    spriteMaterial.depthTest = false;
    return spriteMaterial;
  }
}

export class Dimensions {
  public readonly id: string;
  public readonly group: Group; //contains this.xObject.group, this.yObject.group, this.zObject.group
  public readonly min: Vector3;
  public readonly max: Vector3;

  private readonly xObject: MeasurementObject;
  private readonly yObject: MeasurementObject;
  private readonly zObject: MeasurementObject;
  private isDisposed?: boolean;

  constructor({ min, max }: Box3) {
    this.id = Math.random().toString(32).slice(2);
    this.min = min;
    this.max = max;

    const startPoint = new Vector3(min.x, min.y, min.z);

    this.xObject = new MeasurementObject({
      startPoint,
      endPoint: new Vector3(max.x, min.y, min.z),
      lineColor: defaultXDimensionsColor,
      sphereColor: defaultXDimensionsColor,
    });
    this.xObject.group.visible = true;

    this.yObject = new MeasurementObject({
      startPoint,
      endPoint: new Vector3(min.x, max.y, min.z),
      lineColor: defaultYDimensionsColor,
      sphereColor: defaultYDimensionsColor,
    });
    this.yObject.group.visible = true;

    this.zObject = new MeasurementObject({
      startPoint,
      endPoint: new Vector3(min.x, min.y, max.z),
      lineColor: defaultZDimensionsColor,
      sphereColor: defaultZDimensionsColor,
    });
    this.zObject.group.visible = true;

    this.group = new Group();
    this.group.add(this.xObject.group, this.yObject.group, this.zObject.group);
    this.group.visible = false;
    this.group.name = this.id;
  }

  public get xLength() {
    return this.xObject.rawLength;
  }

  public get yLength() {
    return this.yObject.rawLength;
  }

  public get zLength() {
    return this.zObject.rawLength;
  }

  public makeSpriteMaterial({
    displayUnits,
    nativeUnits,
    dimensionalPrecision,
    displayScale,
  }: {
    displayUnits: UNITS;
    nativeUnits: UNITS;
    dimensionalPrecision: number;
    displayScale: number;
  }): void {
    this.xObject.makeSpriteMaterial({
      displayUnits,
      nativeUnits,
      dimensionalPrecision,
      displayScale,
      messageFillStyle: 'black',
      messageHeader: 'X: ',
      messageHeaderFillStyle: 'red',
    });
    this.yObject.makeSpriteMaterial({
      displayUnits,
      nativeUnits,
      dimensionalPrecision,
      displayScale,
      messageFillStyle: 'black',
      messageHeader: 'Y: ',
      messageHeaderFillStyle: 'green',
    });
    this.zObject.makeSpriteMaterial({
      displayUnits,
      nativeUnits,
      dimensionalPrecision,
      displayScale,
      messageFillStyle: 'black',
      messageHeader: 'Z: ',
      messageHeaderFillStyle: 'blue',
    });
  }

  public scaleForOrthographicCamera({
    viewport,
  }: {
    viewport: ViewPort;
  }): void {
    this.xObject.scaleForOrthographicCamera({ viewport });
    this.yObject.scaleForOrthographicCamera({ viewport });
    this.zObject.scaleForOrthographicCamera({ viewport });
  }

  public doesFitBox3({ box }: { box: Box3 }): boolean {
    const xDiff = box.max.x - box.min.x;
    const yDiff = box.max.y - box.min.y;
    const zDiff = box.max.z - box.min.z;
    const tol = 0.1;
    return (
      isCloseVectors(box.min, this.min, tol) &&
      isClose(this.xObject.rawLength, xDiff, tol) &&
      isClose(this.yObject.rawLength, yDiff, tol) &&
      isClose(this.zObject.rawLength, zDiff, tol)
    );
  }

  //When done with dimensions, it should be disposed
  public dispose() {
    if (this.isDisposed) return;
    this.isDisposed = true;
    for (const obj of [this.xObject, this.yObject, this.zObject]) {
      obj.dispose();
    }
    if (this.group) {
      this.group.clear();
    }
  }
}

//createMeasurement(): helper for handleMeasurementCreate()
const createMeasurement = (
  caliperObjects: [AnyCaliperObject, AnyCaliperObject],
  displayUnits: UNITS,
  nativeUnits: UNITS,
  dimensionalPrecision: number,
  displayScale: number,
  lineColor: Color,
): MeasurementObject | undefined => {
  let result: MeasurementResult | undefined;
  if (
    caliperObjects[0].type !== ENTITY_TYPES.STL &&
    caliperObjects[1].type !== ENTITY_TYPES.STL
  ) {
    result = performCaliperMeasurementStep(caliperObjects);
  } else {
    result = performCaliperMeasurementStl(caliperObjects);
  }

  if (result) {
    const { start, end, comparison, estimated } = result;
    const measurement = new MeasurementObject({
      startPoint: start,
      endPoint: end,
      comparison,
      estimated,
      selections: caliperObjects,
      lineColor,
      sphereColor: lineColor,
    });
    measurement.makeSpriteMaterial({
      displayUnits,
      nativeUnits,
      dimensionalPrecision,
      displayScale,
    });
    return measurement;
  }
};

export const handleMeasurementCreate = (
  renderer: Renderer,
  nextState: RendererState,
): void => {
  // If caliper tools do not indicate a measurement, set active measurement to null
  if (
    (!renderer.state.activeCaliperTool ||
      nextState.caliperObjects.length !== 2) &&
    nextState.historicalCaliperObjects.length !== 2
  ) {
    renderer.state.onSetMeasurement(null);
  }

  // if there are two selected objects and they arent exactly equal to the past state need to make a caliper measurement item
  if (
    renderer.state.activeCaliperTool &&
    nextState.caliperObjects.length === 2 &&
    renderer.state.caliperObjects !== nextState.caliperObjects
  ) {
    const result = createMeasurement(
      nextState.caliperObjects,
      nextState.displayUnits,
      nextState.nativeUnits,
      nextState.dimensionalPrecision,
      nextState.displayScale,
      renderer.state.viewerMode === PART_VIEWER_MODES.VECTOR_EDIT
        ? defaultVectorEditColor
        : defaultMeasurementColor,
    );
    if (result) {
      renderer.state.onSetMeasurement(result);
    }
  }
  // same logic for historical caliper selections
  if (
    nextState.historicalCaliperObjects.length === 2 &&
    renderer.state.historicalCaliperObjects !==
      nextState.historicalCaliperObjects
  ) {
    // TODO: dont recreate if we find it on the array of measurements
    const result = createMeasurement(
      nextState.historicalCaliperObjects,
      nextState.displayUnits,
      nextState.nativeUnits,
      nextState.dimensionalPrecision,
      nextState.displayScale,
      renderer.state.viewerMode === PART_VIEWER_MODES.VECTOR_EDIT
        ? defaultVectorEditColor
        : defaultMeasurementColor,
    );
    if (result) {
      renderer.state.onSetMeasurement(result);
    }
  }
};

/*
Responsible for handling scaling, rendering, and updating all of the dimensions, measurements, and vertex
and intersection constant scaling used in the updateState method of the renderer
*/
// TODO: check dimension scaling with units changes
// TODO: make this much more performant by not checking so much on each update
export const handleMeasurementDimensionVertexUpdate = (
  renderer: Renderer,
  nextState: RendererState,
) => {
  const {
    bodies: nextBodies,
    bodyOrAssemblyInstanceById: nextBodyOrAssemblyInstanceById,
  } = nextState;

  if (!nextState.isTweening) {
    // handle updating scale radius for intersection checks
    const camPositionDifferent = !isCloseVectors(
      renderer.state.cameraPosition,
      nextState.cameraPosition,
    );
    const shouldUpdateScaleRadius =
      camPositionDifferent ||
      renderer.state.cameraZoom !== nextState.cameraZoom;

    if (shouldUpdateScaleRadius) {
      renderer.scaleRadius = nextState.orthoView.getVertexRadius(
        INTERSECTION_SCALE_FACTOR,
      );
    }
  }

  // handle scaling vertexes
  let shouldScaleVertexes =
    renderer.state.cameraZoom !== nextState.cameraZoom ||
    renderer.state.controlTarget !== nextState.controlTarget ||
    renderer.state.orthoView !== nextState.orthoView;

  if (shouldScaleVertexes && !nextState.isTweening) {
    for (const body of nextBodies) {
      if (!body.isLoaded || body.obj instanceof STLBody) {
        continue;
      }
      body.scaleVertexes(
        nextState.orthoView.getVertexRadius(VERTEX_RADIUS_SCALE_FACTOR),
      );
    }
  }

  // updating of text sprite values
  const shouldUpdateSprite =
    renderer.state.displayUnits !== nextState.displayUnits ||
    renderer.state.displayScale !== nextState.displayScale ||
    renderer.state.dimensionalPrecision !== nextState.dimensionalPrecision;

  // handle removing and adding measurement objects
  // show/dispose measurements, only show the latest and/or historical
  const measurementsAreEqual =
    renderer.state.measurement?.id === nextState.measurement?.id;
  const measurementDoesNotExist =
    nextState.measurement &&
    !renderer.scene2.getObjectByName(nextState.measurement.id);

  if (!measurementsAreEqual) {
    if (measurementDoesNotExist && nextState.measurement) {
      renderer.scene2.add(nextState.measurement.group);
      nextState.measurement.group.visible = true;
    }

    // Remove old measurement from scene2 and dispose it
    const measurement = renderer.state.measurement;
    if (measurement) {
      renderer.scene2.remove(measurement.group);
      measurement.dispose();
    }
  }

  if (shouldUpdateSprite) {
    nextState.measurement?.makeSpriteMaterial({
      nativeUnits: nextState.nativeUnits,
      displayUnits: nextState.displayUnits,
      dimensionalPrecision: nextState.dimensionalPrecision,
      displayScale: nextState.displayScale,
    });
  }

  // now scaling of measurements
  const shouldScaleMeasurements = shouldScaleVertexes || !measurementsAreEqual;
  if (shouldScaleMeasurements) {
    nextState.measurement?.scaleForOrthographicCamera({
      viewport: nextState.orthoView,
    });
  }

  // now handle dimensions scaling and show/hide
  // scale and show the dimension, only show the ones that match the current state properties
  // dictated by a display key for visible bodies
  const axisAlignedDimensions = renderer.axisAlignedDimensions;
  if (axisAlignedDimensions) {
    if (shouldUpdateSprite && nextState.showAxisAlignedBoundingBox) {
      axisAlignedDimensions.makeSpriteMaterial({
        displayUnits: nextState.displayUnits,
        nativeUnits: nextState.nativeUnits,
        dimensionalPrecision: nextState.dimensionalPrecision,
        displayScale: nextState.displayScale,
      });
    }

    const shouldScale =
      nextState.showAxisAlignedBoundingBox &&
      (shouldScaleVertexes ||
        nextState.showAxisAlignedBoundingBox !==
          renderer.state.showAxisAlignedBoundingBox);
    if (shouldScale) {
      axisAlignedDimensions.scaleForOrthographicCamera({
        viewport: nextState.orthoView,
      });
    }

    if (!renderer.scene2.getObjectByName(axisAlignedDimensions.id)) {
      renderer.scene2.add(axisAlignedDimensions.group);
    }
    axisAlignedDimensions.group.visible =
      nextState.showAxisAlignedBoundingBox as boolean;
  }

  // handle showing and updating optimal bounding boxes, particularly if active bodies or display conditions change
  const needsOptimalBoxUpdate = areObjectsDifferentForKeys(
    nextState,
    renderer.state,
    [
      'activeBodies',
      'focusedBodies',
      'showOptimalBoundingBox',
      'visibleBodies',
      'displayScale',
      'displayUnits',
      'nativeUnits',
      'dimensionalPrecision',
    ],
  );

  if (needsOptimalBoxUpdate || shouldScaleVertexes) {
    const currentPaintParams = OptimalBoundingBox.getPaintParams({
      displayScale: nextState.displayScale,
      displayUnits: nextState.displayUnits,
      nativeUnits: nextState.nativeUnits,
      dimensionalPrecision: nextState.dimensionalPrecision,
    });

    const { activeBodies, visibleBodies } = nextState;
    const flatBodyIds: string[] = [];
    const flatPartUuids: string[] = [];
    for (const body of nextBodyOrAssemblyInstanceById.values()) {
      if (body.optimalBoundingBox) {
        const obb = body.optimalBoundingBox;
        const isActive = activeBodies.includes(body.id as string);
        if (isActive) {
          if (!renderer.scene2.getObjectByName(obb.id)) {
            renderer.scene2.add(obb.group);
          }

          if (obb.paintParams !== currentPaintParams) {
            obb.makeSpriteMaterial({
              displayScale: nextState.displayScale,
              displayUnits: nextState.displayUnits,
              nativeUnits: nextState.nativeUnits,
              dimensionalPrecision: nextState.dimensionalPrecision,
            });
          }

          if (shouldScaleVertexes) {
            obb.scaleForOrthographicCamera({ viewport: nextState.orthoView });
          }

          // if the active body is an assembly, have it one regardless
          // if its an individual part, have it reflect part visibility
          // for the flat part use case
          const isVisible =
            body instanceof AssemblyInstance ||
            visibleBodies.includes(body.id as string);
          obb.group.visible =
            isVisible && (nextState.showOptimalBoundingBox as boolean);

          // now check for flat body to turn on
          // assumes only one flattened body per formed part
          if (
            body instanceof Body &&
            body.part.flatPartFileUuid &&
            !flatPartUuids.includes(body.part.flatPartFileUuid as string)
          ) {
            flatPartUuids.push(body.part.flatPartFileUuid as string);
            const flatBody = nextBodies.find(
              (bdy) => bdy.part.id === body.part.flatPartFileUuid,
            );
            if (flatBody && flatBody.optimalBoundingBox) {
              flatBodyIds.push(flatBody.id as string);
              const fobb = flatBody.optimalBoundingBox;

              if (!renderer.scene2.getObjectByName(fobb.id)) {
                renderer.scene2.add(fobb.group);
              }

              const isVisible = visibleBodies.includes(flatBody.id as string);
              if (isVisible && fobb.paintParams !== currentPaintParams) {
                fobb.makeSpriteMaterial({
                  displayScale: nextState.displayScale,
                  displayUnits: nextState.displayUnits,
                  nativeUnits: nextState.nativeUnits,
                  dimensionalPrecision: nextState.dimensionalPrecision,
                });
                //Important: if the user performs a sheetmetal interrogation that creates/loads a flat part after the initial partFileUuids load, then:
                //* shouldScaleVertexes must be true so fobb.scaleForOrthographicCamera() is performed. Otherwise the sprites with the optimalBoundingBox's
                //dimensions will not show the first time `showOptimalBoundingBox` is turned on. (If not set to true, then the dimensions do not render until
                //the user pans or zooms. (rotating has no effect)).
                //* There is no issue with rendering the flat part's optimalBoundingBox's dimensions if the flat part exists when the initial partFileUuids are loaded.
                shouldScaleVertexes = true;
              }

              if (shouldScaleVertexes) {
                fobb.scaleForOrthographicCamera({
                  viewport: nextState.orthoView,
                });
              }

              fobb.group.visible =
                isVisible && (nextState.showOptimalBoundingBox as boolean);
            }
          }
        } else {
          // dont turn off flat bodies that have turned on
          if (!flatBodyIds.includes(body.id as string)) {
            obb.group.visible = false;
          }
        }
      }
    }
  }
};
